import { actionTypes, capabilityForReportTypeId, reportTypesForFiltering } from './constants';
import { mapReports, updateReportDownloadProgress } from './helpers';
import { apiCallStatus } from 'helpers/constants';

const initialState = {
    list: {
        data: [],
        error: null,
        loadingStatus: null
    },
    downloadReport: {
        data: null,
        error: null,
        loadingStatus: null
    },
    isFilterVisible: true,
    filters: {
        searchboxFilter: '',
        dateFilter: { from: undefined, to: undefined },
        reportingPeriodFilter: { from: undefined, to: undefined },
        reportTypeFilter: reportTypesForFiltering[0],
        reportStatusFilter: { reportStateId: undefined, reportStatusName: undefined },
    },
    reportGenerator: {
        isModalOpen: false,
        preselectedAsset: null,
        capabilities:  //Creates default data object for each supported capabilityType. Needed for consistent length loading state in redux
            Object.keys(capabilityForReportTypeId).reduce((acc, capabilityForReportTypeId) => (
                {
                    ...acc,
                    [capabilityForReportTypeId]: { data: [], loadingStatus: null }
                }), {}),
        selectedReportType: null,
        loadingStatus: null,
        error: null
    },
    reportUpload: {
        isModalOpen: false,
        selectedReportType: null,
        reportingPeriod: { from: undefined, to: undefined },
        loadingStatus: null,
        error: null,
    },
    publishReportLoadingStatus: null,
    publishReportError: null,
    rejectReportLoadingStatus: null,
    rejectReportError: null,
    reportCapabilities: {
        data: [],
        error: null,
        loadingStatus: apiCallStatus.LOADING
    },
    sensorOfAssets: {
        data: [],
        error: null,
        loadingStatus: null
    },
    deleteReportLoadingStatus: null,
    deleteReportError: null,
    rawDataCollections: {
        data: [],
        error: null,
        loadingStatus: null
    },
};

export const reducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.GET_REPORTS: {
            // Keep showing the previous list while loading/erroring, so the UI doesn't flash to an empty state
            const hasNewData = action.loadingStatus === apiCallStatus.SUCCESS;
            return {
                ...state,
                list: {
                    ...state.list,
                    data: hasNewData ? mapReports(action.data, state.reportCapabilities.data) : state.list.data,
                    error: action.error ? {
                        statusCode: action.error?.status || action.error?.statusCode,
                        message: action.error?.message
                    } : null,
                    loadingStatus: action.loadingStatus
                }
            };
        }
        case actionTypes.SET_FILTERS: {
            return {
                ...state,
                filters: { ...state.filters, ...action.filters }
            };
        }
        case actionTypes.OPEN_GENERATOR_MODAL: {
            return {
                ...state,
                reportGenerator: {
                    ...state.reportGenerator,
                    isModalOpen: true,
                    preselectedAsset: action.preselectedAsset || null,
                    loadingStatus: null,
                    error: null
                }
            };
        }
        case actionTypes.CLOSE_GENERATOR_MODAL: {
            return {
                ...state,
                reportGenerator: { ...state.reportGenerator, isModalOpen: false, preselectedAsset: null }
            };
        }
        case actionTypes.GET_CAPABILITIES: {
            return {
                ...state,
                reportGenerator: {
                    ...state.reportGenerator,
                    capabilities: {
                        ...state.reportGenerator.capabilities,
                        ...action.data.reportTypeId && { [action.data.reportTypeId]: { data: action.data.capabilities, loadingStatus: action.loadingStatus } }
                    }
                }
            };
        }
        case actionTypes.OPEN_UPLOAD_MODAL: {
            return {
                ...state,
                reportUpload: { ...state.reportUpload, isModalOpen: true }
            };
        }
        case actionTypes.CLOSE_UPLOAD_MODAL: {
            return {
                ...state,
                reportUpload: { ...state.reportUpload, isModalOpen: false }
            };
        }
        case actionTypes.SET_REPORT_GENERATION_TYPE: {
            return {
                ...state,
                reportGenerator: {
                    ...state.reportGenerator,
                    selectedReportType: action.reportType
                }
            };
        }
        case actionTypes.SET_REPORT_UPLOAD_TYPE: {
            return {
                ...state,
                reportUpload: {
                    ...state.reportUpload,
                    selectedReportType: action.reportType
                }
            };
        }
        case actionTypes.UPLOAD_REPORT: {
            return {
                ...state,
                reportUpload: {
                    ...state.reportUpload,
                    loadingStatus: action.loadingStatus,
                    error: action.error || null
                }
            };
        }
        case actionTypes.CLEAR_REPORT_UPLOAD: {
            return {
                ...state,
                reportUpload: {
                    ...state.reportUpload,
                    loadingStatus: null,
                    error: null
                }
            };
        }
        case actionTypes.GENERATE_REPORT:
        case actionTypes.GENERATE_FLEET_REPORT:
        case actionTypes.GENERATE_FLEET_DETAILED_REPORT: {
            return {
                ...state,
                reportGenerator: {
                    ...state.reportGenerator,
                    loadingStatus: action.loadingStatus,
                    error: action.error || null
                }
            };
        }
        case actionTypes.CLEAR_REPORT_GENERATION: {
            return {
                ...state,
                reportGenerator: {
                    ...state.reportGenerator,
                    loadingStatus: null,
                    error: null
                }
            };
        }
        case actionTypes.TOGGLE_FILTER_VISIBILITY:
        case actionTypes.SET_FILTER_VISIBILITY_FROM_LS: {
            return {
                ...state,
                isFilterVisible: action.isFilterVisible
            };
        }
        case actionTypes.DOWNLOAD_REPORT: {
            return {
                ...state,
                list: {
                    ...state.list,
                    data: [...updateReportDownloadProgress(state.list.data, action.data, action.loadingStatus)]
                },
                downloadReport: {
                    data: action.data || null,
                    error: action.error || null,
                    loadingStatus: action.loadingStatus
                }
            };
        }
        case actionTypes.CLEAR_DOWNLOAD_REPORT: {
            return {
                ...state,
                reportCapabilities: {
                    ...initialState.reportCapabilities
                },
                downloadReport: {
                    data: null,
                    error: null,
                    loadingStatus: null
                }
            };
        }
        case actionTypes.PUBLISH_REPORT: {
            return {
                ...state,
                publishReportLoadingStatus: action.loadingStatus,
                publishReportError: action.error || null
            };
        }
        case actionTypes.CLEAR_PUBLISH_REPORT: {
            return {
                ...state,
                publishReportLoadingStatus: null,
                publishReportError: null
            };
        }
        case actionTypes.REJECT_REPORT: {
            return {
                ...state,
                rejectReportLoadingStatus: action.loadingStatus,
                rejectReportError: action.error || null
            };
        }
        case actionTypes.CLEAR_REJECT_REPORT: {
            return {
                ...state,
                rejectReportLoadingStatus: null,
                rejectReportError: null
            };
        }
        case actionTypes.GET_REPORT_CAPABILITIES: {
            const updatedCapabilities = action?.data || [];
            // Only re-map existing reports if we have both reports and capabilities
            // This ensures delete buttons appear when capabilities load after reports
            const shouldRemapReports = state?.list?.data?.length > 0 && updatedCapabilities?.length > 0;
            const remappedReports = shouldRemapReports
                ? mapReports(state?.list?.data, updatedCapabilities)
                : state?.list?.data;

            return {
                ...state,
                reportCapabilities: {
                    ...state.reportCapabilities,
                    data: updatedCapabilities,
                    error: action.error,
                    loadingStatus: action.loadingStatus
                },
                list: {
                    ...state.list,
                    data: remappedReports
                }
            };
        }
        case actionTypes.GET_SENSOR_OF_ASSETS: {
            return {
                ...state,
                sensorOfAssets: {
                    ...state.sensorOfAssets,
                    data: action.data,
                    error: action.error,
                    loadingStatus: action.loadingStatus
                }
            };
        }
        case actionTypes.DELETE_REPORT: {
            return {
                ...state,
                deleteReportLoadingStatus: action.loadingStatus,
                deleteReportError: action.error || null
            };
        }
        case actionTypes.CLEAR_DELETE_REPORT: {
            return {
                ...state,
                deleteReportLoadingStatus: null,
                deleteReportError: null
            };
        }
        case actionTypes.GET_RAW_DATA_COLLECTIONS: {
            return {
                ...state,
                rawDataCollections: { ...action }
            };
        }
        case actionTypes.CLEAR_RAW_DATA_COLLECTIONS: {
            return {
                ...state,
                rawDataCollections: {
                    data: [],
                    error: null,
                    loadingStatus: null
                }
            };
        }
        default:
            return state;
    }
};
